<template>
  <div style="width: 100%; height: 100%;">
    <div style="width: 100%; height: 100%;">
      <div ref="ganttContainer" style="width: 100%; height: 600px;"></div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { BSpinner } from "bootstrap-vue";
import gantt from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import dayjs from "dayjs";

export default {
  name: "DHTMLXGanttChart",
  components: {
    BSpinner,
  },
  data() {
    return {
      id: this.$route.params.id,
      ganttInstance: null,
      ganttTasks: [],
      ganttLinks: [],
    };
  },
  computed: {
    ...mapGetters("projectModule", {
      tasks: "tasks",
      milestones: "milestones",
      loading: "loading",
    }),
  },
  methods: {
    ...mapActions("projectModule", [
      "getProjectTasksAction",
      "getProjectMilestoneAction",
    ]),

    transformData() {
      let data = [];
      let links = [];

      this.milestones.forEach((milestone) => {
        const startDate = dayjs(milestone.startDate).startOf("day");
        const endDate = dayjs(milestone.endDate).startOf("day");

        if (!startDate.isValid() || !endDate.isValid()) {
          console.warn(`Skipping invalid milestone: ${milestone.name}`);
          return;
        }

        data.push({
          id: milestone.id,
          text: `Milestone: ${milestone.name}`,
          start_date: startDate.toDate(),
          duration: endDate.diff(startDate, "days") + 1,
          rawEndDate: milestone.endDate, // ✅ store original string
          progress: 1,
          open: true,
        });
      });

      this.tasks.forEach((task) => {
        const startDate = dayjs(task.startDate).startOf("day");
        const endDate = dayjs(task.endDate).startOf("day");

        if (!startDate.isValid() || !endDate.isValid()) {
          console.warn(`Skipping invalid task: ${task.title}`);
          return;
        }

        data.push({
          id: task.id,
          text: `Task: ${task.title}`,
          start_date: startDate.toDate(),
          duration: endDate.diff(startDate, "days") + 1,
          rawEndDate: task.endDate, // ✅ store original string
          progress: Math.random(),
          parent: task.milestonesId || 0,
          assignee: task.assigneeName || "Unassigned",
        });

        if (task.milestonesId) {
          links.push({
            id: `link-${task.id}`,
            source: task.milestonesId,
            target: task.id,
            type: "0",
          });
        }
      });

      data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      this.ganttTasks = { data };
      this.ganttLinks = { links };
    },

    renderGantt() {
      this.$nextTick(() => {
        gantt.config.readonly = true;
        gantt.config.drag_move = false;
        gantt.config.drag_resize = true;
        gantt.config.drag_progress = false;
        gantt.config.show_task_cells = false;
        gantt.config.grid_resize = true;  // Enable grid resizing
        gantt.config.grid_resizer = true; // Enable column resizer

        gantt.config.columns = [
          { name: "text", label: "Task Name", tree: true, width: 250, resize: true },
          { name: "start_date", label: "Start Date", align: "center", width: 120, resize: true },
          {
            name: "end_date",
            label: "End Date",
            align: "center",
            width: 120,
            resize: true,
            template: (task) => dayjs(task.rawEndDate).format("YYYY-MM-DD"), // ✅ shows correct value
          },
          { name: "assignee", label: "Assignee", align: "center", width: 180, resize: true },
        ];

        gantt.init(this.$refs.ganttContainer);
        gantt.clearAll();
        gantt.parse({ data: this.ganttTasks.data, links: this.ganttLinks.links });
      });
    }

  },
  async mounted() {
    try {
      const loader = this.$swal.fire({
        title: "Processing...",
        text: "Please wait while the data is being fetched",
        didOpen: () => {
          this.$swal.showLoading();
        },
        allowOutsideClick: () => !this.$swal.isLoading(),
        buttonsStyling: false,
        showConfirmButton: false,
      });

      await this.getProjectTasksAction({ id: this.id });
      await this.getProjectMilestoneAction(this.id);
      this.transformData();
      this.renderGantt();
      loader.close();
    } catch (error) {
      console.log(error);
      loader.close();
    }
  },
};
</script>

<style>
.gantt_task_line.milestone {
  background: #007bff !important;
  border: 1px solid #0056b3 !important;
}

.gantt_grid_column_resize {
  cursor: ew-resize !important;
}

/* Enable column resizing cursor */
.gantt_grid_column_resize {
  cursor: ew-resize !important;
}

/* Ensure resizing works properly */
.gantt_grid_head_cell {
  position: relative;
  border-right: 1px solid #ccc;
  cursor: ew-resize;
}

/* Styling to show resizer */
.gantt_grid_head_cell::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  background: transparent;
  cursor: ew-resize;
}
</style>
